import type { OrganizationDTO } from '@/api/trafikportalen';

/**
 * Check if an organization is in the inactivating state
 * (inactive state but less than 15 minutes since update)
 */
export const isOrganizationInactivating = (
  organization: OrganizationDTO,
): boolean => {
  if (organization.state !== 'inactive') {
    return false;
  }

  const updatedAt = new Date(organization.updated_at);
  const now = new Date();
  const fifteenMinutes = 15 * 60 * 1000;
  const timeSinceUpdate = now.getTime() - updatedAt.getTime();

  return timeSinceUpdate < fifteenMinutes;
};

export const getOrganizationEffectiveState = (
  organization: OrganizationDTO,
): 'approved' | 'inactive' | 'inactivating' | 'submitted' | 'rejected' => {
  if (isOrganizationInactivating(organization)) {
    return 'inactivating';
  }
  return organization.state;
};

export const organizationHasStudentAccess = (
  organization: OrganizationDTO,
): boolean => (organization.enabled_collection_types?.length ?? 0) > 0;

/**
 * Approved organisations can be issued student tickets. `inactive` already
 * covers the inactivating window, when existing tickets are being revoked,
 * and submitted or rejected organisations are not approved.
 */
export const organizationCanReceiveStudentTickets = (
  organization: OrganizationDTO,
): boolean => organization.state === 'approved';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const PAYER_LIST_HIDE_INACTIVE_ORG_DAYS = 60 * MS_PER_DAY;
export const isOrganizationInactiveBeyondPayerListThreshold = (
  organization: OrganizationDTO,
): boolean => {
  if (organization.state !== 'inactive') {
    return false;
  }
  const msSinceUpdate =
    Date.now() - new Date(organization.updated_at).getTime();
  return msSinceUpdate >= PAYER_LIST_HIDE_INACTIVE_ORG_DAYS;
};

export const getRemainingInactivationTime = (
  organization: OrganizationDTO,
): number => {
  if (!isOrganizationInactivating(organization)) {
    return 0;
  }

  const updatedAt = new Date(organization.updated_at);
  const now = new Date();
  const fifteenMinutesInMs = 15 * 60 * 1000 - 1;
  const timeSinceUpdate = now.getTime() - updatedAt.getTime();

  return Math.max(0, fifteenMinutesInMs - timeSinceUpdate);
};
